package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.layout.Box
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.MenuDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.tooling.preview.Preview
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme

/**
 * One entry of an [AppOverflowMenu]. [label] and the optional [iconName] are caller-owned
 * (localized text comes from resources); [destructive] paints the row in the error color for
 * actions like Delete / Block.
 */
data class AppMenuItem(
    val label: String,
    val onClick: () -> Unit,
    val iconName: String? = null,
    val destructive: Boolean = false,
    val enabled: Boolean = true,
)

/**
 * Three-dot ("more") overflow: a tokenized icon button that opens a dropdown of [items]. The one
 * place for secondary row actions (report, block, edit, delete, …) so cards stay quiet and new
 * actions are a list entry, not another big button. [contentDescription] labels the trigger for
 * screen readers and must be localized by the caller.
 */
@Composable
fun AppOverflowMenu(
    items: List<AppMenuItem>,
    contentDescription: String,
    modifier: Modifier = Modifier,
    iconName: String = "more_vert",
    tint: Color = AppTheme.colors.onSurfaceVariant,
) {
    var expanded by remember { mutableStateOf(false) }
    Box(modifier = modifier) {
        AppIconButton(
            iconName = iconName,
            contentDescription = contentDescription,
            onClick = { expanded = true },
            tint = tint,
        )
        DropdownMenu(
            expanded = expanded,
            onDismissRequest = { expanded = false },
            shape = AppTheme.shapes.md,
            containerColor = AppTheme.colors.surface,
            shadowElevation = AppTheme.elevation.level2,
        ) {
            items.forEach { item ->
                val color = if (item.destructive) AppTheme.colors.error else AppTheme.colors.onSurface
                DropdownMenuItem(
                    text = { Text(text = item.label, style = AppTheme.typography.bodyMd, color = color) },
                    onClick = {
                        expanded = false
                        item.onClick()
                    },
                    enabled = item.enabled,
                    leadingIcon = item.iconName?.let { name ->
                        { AppIcon(name = name, size = AppTheme.sizes.iconMd, tint = color) }
                    },
                    colors = MenuDefaults.itemColors(
                        textColor = color,
                        leadingIconColor = color,
                        disabledTextColor = AppTheme.colors.outlineVariant,
                        disabledLeadingIconColor = AppTheme.colors.outlineVariant,
                    ),
                )
            }
        }
    }
}

@Preview
@Composable
private fun AppOverflowMenuPreview() {
    AppTheme {
        AppOverflowMenu(
            items = listOf(
                AppMenuItem(label = "Report", iconName = "flag", onClick = {}),
                AppMenuItem(label = "Block", iconName = "block", destructive = true, onClick = {}),
            ),
            contentDescription = "More",
        )
    }
}
